import { BlockProductFeedEntityHydrated, EntityId } from "@jackfruit/common"
import React from "react"
import { useLocation } from "react-use"
import Wrapper from "~/components/Wrapper"
import { useCurrentAvailableProductsForPageAndDefaultFultillment } from "~/hooks/useCurrentAvailableProductsForPageAndDefaultFultillment"
import { useCurrentAvailableProductsForPageRegardlessOfFulfillment } from "~/hooks/useCurrentAvailableProductsForPageRegardlessOfFulfillment"
import { useForceCanonicalForPage } from "~/hooks/useForceCanonicalForPage"
import { usePageSession } from "~/hooks/usePageSession"
import { useProduct } from "~/hooks/useProduct"
import { useProductIdFromUrl } from "./hooks/useProductIdFromUrl"
import ProductFeedContent from "./ProductFeedContent"
import ProductFeedNotFound from "./ProductFeedNotFound"
import ProductFeedSkeleton from "./ProductFeedSkeleton"

interface Props {
  config: BlockProductFeedEntityHydrated
  pageId: EntityId
}

const ProductFeedBlock: React.FC<Props> = ({ pageId, config }) => {
  const { href } = useLocation()
  useForceCanonicalForPage(pageId, href ?? "")

  const productId = useProductIdFromUrl()
  const {
    pageSession: { isReady },
  } = usePageSession()

  const { products: availableProductsForDefaultFulfillment } =
    useCurrentAvailableProductsForPageAndDefaultFultillment()

  const { products: allAvailableProductsForPage } =
    useCurrentAvailableProductsForPageRegardlessOfFulfillment()

  const { product } = useProduct(productId)

  const productIdToUse = Boolean(productId)
    ? productId
    : availableProductsForDefaultFulfillment[0]?.id

  let isProductEnabledForPage = Boolean(
    allAvailableProductsForPage.find(
      product => product.id === Number(productIdToUse)
    )
  )

  // product is marked as not found if:
  // - product is not present remotely or locally
  // - product is not found in the available products for the page
  const cannotFindProduct =
    (!Boolean(product) || !isProductEnabledForPage) && isReady

  let isProductValid = true
  if (product) {
    const { metaData } = product
    const { merchantAvailability, gtin } = metaData

    if (!merchantAvailability) {
      isProductValid = false
      console.log("product merchant availability is disabled")
    }
    if (!gtin) {
      isProductValid = false
      console.log("product gtin is missing")
    }
  }

  const shouldShowProductNotFound =
    productId && (!isProductValid || cannotFindProduct)

  let component = <ProductFeedSkeleton />
  if (shouldShowProductNotFound && productId) {
    component = <ProductFeedNotFound />
  } else if (isReady) {
    component = (
      <ProductFeedContent
        pageId={pageId}
        productId={productIdToUse}
        config={config}
      />
    )
  }

  return (
    <Wrapper
      maxWidth="1000px"
      minHeight={{ base: "780px", md: "560px" }}
      borderBottomWidth={1}
    >
      {component}
    </Wrapper>
  )
}

export default ProductFeedBlock
